import { Configuration as msalConfiguration } from "@azure/msal-browser";

const isProduction = import.meta.env.PROD;

// If development, use localhost
const redirectUrl = isProduction
	? (import.meta.env.VITE_MSAL_REDIRECT_URL ?? "")
	: "http://localhost:3009";

/**
 * A required MSAL setting, or a message naming what is missing.
 *
 * MSAL does not check these. An unset authority reaches `formatAuthorityUri`,
 * which calls `.endsWith` on it and throws
 * `Cannot read properties of undefined (reading 'endsWith')` from inside the
 * library — a stack trace with nothing in it about `.env`, on the sign-in
 * button, on a fresh clone. `clientId` fell back to `""` and failed just as
 * opaquely a moment later.
 *
 * Thrown at module load rather than logged: there is no useful app without
 * auth, and a warning scrolls past while the button quietly does nothing.
 */
function required(name: string, value: string | undefined): string {
	if (!value?.trim()) {
		throw new Error(
			`${name} is not set. Copy .env.example to .env and restart the dev ` +
				`server — Vite only reads .env at startup.`,
		);
	}
	return value;
}

export const msal: msalConfiguration = {
	auth: {
		clientId: required(
			"VITE_MSAL_CLIENT_ID",
			import.meta.env.VITE_MSAL_CLIENT_ID,
		),
		authority: required(
			"VITE_MSAL_AUTHORITY",
			import.meta.env.VITE_MSAL_AUTHORITY,
		),
		redirectUri: redirectUrl,
	},
	cache: {
		cacheLocation: "sessionStorage",
		storeAuthStateInCookie: false,
	},
};

export const loginRequest = {
	scopes: ["User.Read"],
};

// S2 API configuration (pymble-apis / FastAPI)
export const api_s2 = {
	/**
	 * Which API this build talks to.
	 *
	 * The env var wins everywhere, including production. It used to be read
	 * only outside production, with the live API hardcoded — so a deployed
	 * build could not be pointed at staging without editing this file, which
	 * is the whole reason a deploy-time variable exists.
	 *
	 * The defaults still hold when nothing is set: the live API for a
	 * production build, a local API otherwise. So an unconfigured deploy goes
	 * to production rather than somewhere surprising.
	 */
	baseURL:
		import.meta.env.VITE_API_S2_BASE_URL ??
		(isProduction
			? "https://papi.pymblelc.nsw.edu.au/"
			: "http://localhost:8000/"),
	scopes: [
		import.meta.env.VITE_API_S2_SCOPES ?? "api://pymble-apis/access",
	],
};

/**
 * AI agents this app talks to, by their `MyPymble.Agents` `_id`.
 *
 * An agent is a database record — instructions, model, provider — created in
 * the portal's agent editor, not in this repo. The id is the whole of the
 * reference, and the API decides what that agent may do for the caller: naming
 * one here grants nothing.
 *
 * FROM THE ENVIRONMENT, not hardcoded as the portal's own agent table is. The
 * record differs per environment — a staging agent points at a staging model
 * deployment — and an id compiled into the bundle would send local development
 * at whatever production is running. Absent is a legitimate state: the panel
 * reads it as "not configured here" and says so, rather than failing on send.
 */
export const agents = {
	/** Event Scheduling Assistant — the create flow's chat panel. */
	eventAssistant: import.meta.env.VITE_EVENT_ASSISTANT_AGENT_ID?.trim() ?? "",
};

/**
 * The ChatKit widget's domain key.
 *
 * NOT A SECRET — it is published to the browser and its only job is to scope the
 * widget to the domains we have registered against it. Hardcoded rather than an
 * env var for exactly that reason, and to match my-pymble-portal, which carries
 * the same value: the two apps share one key.
 *
 * ⚠️ AN UNREGISTERED ORIGIN FAILS SILENTLY. Both this app's hosts — production
 * and `http://localhost:3009` — must be on the key's allowed-domains list in the
 * OpenAI dashboard. Without that the widget simply never initialises: an empty
 * box, no composer, no console error, and nothing to suggest the cause is a
 * setting on somebody's dashboard rather than a bug in this repo.
 */
export const chatkitDomainKey =
	"domain_pk_68f87d17453481909ed174659c43883c0937b33ca95abd31";

/**
 * A number out of the environment, falling back when it is absent or unusable.
 *
 * Env values arrive as strings and are hand-edited, so an empty, blank or
 * mistyped one is a real possibility. Falling back beats letting `NaN` through:
 * a NaN map centre is rejected by Google and shows a broken image, which reads
 * as a bug in the app rather than a typo in a config file.
 */
const envNumber = (value: string | undefined, fallback: number): number => {
	const parsed = Number(value?.trim());
	return value?.trim() && Number.isFinite(parsed) ? parsed : fallback;
};

// App configuration
export const app = {
	name: "Event Manager",
	clientId: "event-manager-v3",
	colorPrimary: "#002f67",
	defaultGroupIcon:
		"https://pymblelc.blob.core.windows.net/assets/pymble-p.png",
	// Served from this app: the school website's theme paths have moved before
	// (the previous hotlink 404s), and the header should not vanish with them.
	logoUrl: "/plc-logo-white.svg",
	privacyPolicyUrl:
		"https://www.pymblelc.nsw.edu.au/wp-content/uploads/2021/02/Privacy-Policy.pdf",
	/** Notice Manager — "Create Notice" hands the event over to it (as in v2). */
	noticesUrl: "https://notices.pymblelc.nsw.edu.au/notices",
	googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? "",
	/**
	 * Where the Address tab's map points before an event has a location of its
	 * own. Most events are held on site, so the campus is a useful starting view
	 * rather than an arbitrary one.
	 *
	 * The fallback is the campus, and matches the coordinates
	 * my-pymble-portal uses for its own `app.defaultLocation` so the two apps
	 * agree on where the school is. Overridable per environment for anyone
	 * running this against a different site.
	 */
	defaultLocation: {
		lat: envNumber(import.meta.env.VITE_DEFAULT_MAP_LAT, -33.745897),
		lng: envNumber(import.meta.env.VITE_DEFAULT_MAP_LNG, 151.136852),
		/* Named here rather than written into the Address tab, so that
		   overriding the coordinates above cannot leave a caption pointing at
		   somewhere the map is no longer showing. */
		label:
			import.meta.env.VITE_DEFAULT_MAP_LABEL?.trim() ||
			"Pymble Ladies’ College",
	},
};
